// languagetools:disable

'use client'

import { usePathname, useRouter } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import type { Channel as ChannelInstance, Event, MessageResponse } from 'stream-chat'
import { StreamChat } from 'stream-chat'

import { envConfig } from '../lib/env-config'
import { tokenUtils } from '../utils/token-utils'
import { useBrowserNotification } from './use-browser-notification'

const ACTIVE_CHAT_KEY = 'activeChatID'

/**
 * Bootstraps the StreamChat singleton and manages all client-level event listeners
 * for in-app and browser push notifications.
 *
 * Responsibilities:
 * - Creates (or returns the existing) StreamChat singleton via `getInstance`
 * - Tracks the currently active chat channel so notifications are suppressed
 *   for messages the user is already viewing
 * - Fires browser push notifications for `message.new` and
 *   `notification.message_new` events from other users
 * - Syncs `activeChatID` across tabs via the `storage` event
 * - Clears `activeChatID` from localStorage when the page is about to unload
 */
export function useChatClient() {
  const router = useRouter()
  const pathname = usePathname()
  const { create } = useBrowserNotification()

  // `getInstance` returns the existing client if one has already been created with
  // this app key, ensuring only one WebSocket connection is opened per tab.
  const [client] = useState(() =>
    StreamChat.getInstance(envConfig.stream.appKey ?? '', {
      enableWSFallback: true,
      timeout: 30000
    })
  )

  const [activeChatID, setActiveChatID] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACTIVE_CHAT_KEY)
    }
    return null
  })

  // Refs to avoid stale closures in event handlers registered once.
  // Updating the ref on every render is cheaper than re-registering handlers.
  const activeChatIDRef = useRef(activeChatID)
  activeChatIDRef.current = activeChatID

  const pathnameRef = useRef(pathname)
  pathnameRef.current = pathname

  const loggedInUserID = tokenUtils.getUserID() || ''

  /**
   * Returns true if the message carries an `action_type` field, indicating it is
   * a system/automated action message rather than a user-authored chat message.
   * Action messages are excluded from notifications.
   */
  const isActionMessage = (event: Event): boolean => {
    const message = event.message as MessageResponse & { action_type?: string }
    return !!message?.action_type
  }

  /**
   * Fires a browser push notification for an incoming message.
   * `includeSilent` controls whether the `silent` flag from the message payload
   * is forwarded — used to suppress sound on `message.new` vs `notification.message_new`.
   */
  const createMessageNotification = useCallback(
    (event: Event, includeSilent: boolean) => {
      if (!event.message?.user) return
      create({
        body: event.message.text || '',
        data: {
          messageID: event.message.id,
          ...(includeSilent && { silent: event.message.silent })
        },
        notificationHandler: () => {
          router.push(`/user/inbox?chatID=${event.channel_id}&chatType=${event.channel_type}`)
        },
        title: event.message.user?.name ? `${event.message.user.name} sent a message` : ''
      })
    },
    [create, router]
  )

  /**
   * Handles `message.new` events fired when a message arrives in a channel the
   * user is already a member of and has open.
   * Notification is skipped if:
   * - The message is an action message
   * - The sender is the logged-in user
   * - The message arrived in the currently active chat channel
   */
  const handleNewMessage = useCallback(
    (event: Event) => {
      if (isActionMessage(event)) return
      if (
        loggedInUserID !== event?.user?.id &&
        event.message &&
        event.message.user &&
        event.channel_id !== activeChatIDRef.current
      ) {
        createMessageNotification(event, true)
      }
    },
    [loggedInUserID, createMessageNotification]
  )

  /**
   * Handles `notification.message_new` events fired when a message arrives in a
   * channel the user is a member of but does not currently have open (background tab).
   * Similar suppression rules as `handleNewMessage` but without the `silent` flag.
   */
  const handleNewMessageNotification = useCallback(
    (event: Event) => {
      if (isActionMessage(event)) return
      if (
        event.message &&
        event.message.user &&
        loggedInUserID !== event?.message.user.id &&
        event.channel_id !== activeChatIDRef.current
      ) {
        createMessageNotification(event, false)
      }
    },
    [loggedInUserID, createMessageNotification]
  )

  /**
   * Keeps `activeChatID` in sync across browser tabs via the `storage` event.
   * Only processes updates that did not originate from an inbox-routed URL to
   * avoid conflicts when the inbox tab itself manages the active chat state.
   */
  const handleStorageChange = useCallback((event: StorageEvent) => {
    if (event.key === ACTIVE_CHAT_KEY && event.url?.indexOf('inbox') !== 0) {
      setActiveChatID(event.newValue)
    }
  }, [])

  /**
   * Clears the active chat ID from localStorage when the page unloads,
   * but only if the user is not currently on the inbox route (where the
   * inbox component manages its own active chat persistence).
   */
  const handleBeforeUnload = useCallback(() => {
    if (!pathnameRef.current.startsWith('/inbox')) {
      localStorage.setItem(ACTIVE_CHAT_KEY, '')
    }
  }, [])

  /**
   * Attaches all client-level Stream Chat and window event listeners.
   * Called once from `ChatProvider` on mount.
   */
  const addListeners = useCallback(() => {
    client.on('message.new', handleNewMessage)
    client.on('notification.message_new', handleNewMessageNotification)
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('beforeunload', handleBeforeUnload)
  }, [client, handleNewMessage, handleNewMessageNotification, handleStorageChange, handleBeforeUnload])

  /**
   * Removes all client-level event listeners attached by `addListeners`.
   * Called from `ChatProvider` on unmount to prevent memory leaks.
   */
  const removeListeners = useCallback(() => {
    client.off('message.new', handleNewMessage)
    client.off('notification.message_new', handleNewMessageNotification)
    window.removeEventListener('storage', handleStorageChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [client, handleNewMessage, handleNewMessageNotification, handleStorageChange, handleBeforeUnload])

  /**
   * Updates the currently active chat channel.
   * Persists the channel ID to localStorage (outside inbox routes) so other tabs
   * can suppress duplicate notifications via `handleStorageChange`.
   */
  const setActiveChat = useCallback((channel: ChannelInstance | null) => {
    const newActiveChatID = channel?.id || null
    setActiveChatID(newActiveChatID)

    if (!pathnameRef.current.startsWith('/inbox')) {
      localStorage.setItem(ACTIVE_CHAT_KEY, newActiveChatID || '')
    }
  }, [])

  return {
    activeChatID,
    addListeners,
    client,
    removeListeners,
    setActiveChat
  }
}
